import { useEffect, useMemo, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import useTableState from '../../hooks/useTableState';
import { getMyDownline } from '../../api/userApi';
import dayjs from 'dayjs';
import { facetFilterOptions } from '../common/facetFilter';

// My Team — the people the signed-in user recruited.
//
// Shaped like the user list, deliberately minus every commission rate: an upline
// has no business reading their downline's PSC/DOC/TOC/MOC off a roster. The
// server never projects them, so there is no visibility rule here to get wrong.
//
// Not the agent hierarchy either. That page pulls every user, sale, buddy and
// manager assignment to the browser and filters in JavaScript; here the server
// returns only the caller's own rows. (user 2026-09-09)

// TWO status columns, because they answer different questions:
//   Approval — the application: did they get through onboarding?
//   Status   — the account: are they switched on right now?
// The user list only has the second. Someone who has applied but has no account
// yet shows an Approval and a blank Status, and appears nowhere else their
// upline can reach.
const APPROVAL_STYLE = {
  Approved:      { label: 'Approved',       fg: '#065F46', bg: '#D1FAE5' },
  PendingReview: { label: 'Pending review', fg: '#92400E', bg: '#FEF3C7' },
  Rejected:      { label: 'Rejected',       fg: '#991B1B', bg: '#FEE2E2' },
};

const STATUS_STYLE = {
  Active:        { label: 'Active',         fg: '#065F46', bg: '#D1FAE5' },
  PendingReview: { label: 'Pending review', fg: '#92400E', bg: '#FEF3C7' },
  Inactive:      { label: 'Inactive',       fg: '#92400E', bg: '#FEF3C7' },
  Rejected:      { label: 'Rejected',       fg: '#991B1B', bg: '#FEE2E2' },
  Archived:      { label: 'Archived',       fg: '#64748B', bg: '#F1F5F9' },
};

const QUIZ_STYLE = {
  Passed:    { label: 'Passed',     fg: '#065F46', bg: '#D1FAE5' },
  NotPassed: { label: 'Not passed', fg: '#991B1B', bg: '#FEE2E2' },
};

const pill = (raw, styles, fallback = '—') => {
  if (!raw) return <span style={{ color: '#CBD5E1' }}>{fallback}</span>;
  const s = styles[raw] ?? { label: raw, fg: '#475569', bg: '#E2E8F0' };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        backgroundColor: s.bg, color: s.fg, fontWeight: 600,
        fontSize: '0.7rem', height: 22, fontFamily: "'Poppins', sans-serif",
      }}
    />
  );
};

const money = (v) =>
  v == null ? '—' : 'RM ' + Number(v).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MyTeam() {
  const ts = useTableState('myTeam', { defaultSorting: [{ id: 'name', desc: false }] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getMyDownline();
        if (alive) setRows(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (alive) setError("Couldn't load your team. Refresh to try again.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const columns = useMemo(() => [
    {
      id: 'name', header: 'Name', accessorKey: 'name', size: 200,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <Box sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#0F172A' }}>
            {row.original.name || '—'}
          </Box>
          <Box sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>
            {row.original.username || ''}
          </Box>
        </Box>
      ),
    },
    {
      // Tap-to-call on a phone; the roster is mostly used on mobile.
      id: 'phone', header: 'Phone', size: 140,
      accessorFn: r => r.phone || '',
      Cell: ({ cell }) => {
        const v = cell.getValue();
        if (!v) return <span style={{ color: '#CBD5E1' }}>—</span>;
        return <a href={`tel:${String(v).replace(/[^+\d]/g, '')}`} style={{ color: '#0369A1', textDecoration: 'none' }}>{v}</a>;
      },
    },
    {
      id: 'role', header: 'Role', size: 140,
      accessorFn: r => r.role || '',
      Cell: ({ cell }) => cell.getValue() || <span style={{ color: '#CBD5E1' }}>—</span>,
    },
    {
      id: 'group', header: 'Group', size: 130,
      accessorFn: r => r.groupName || '',
      Cell: ({ cell }) => cell.getValue() || <span style={{ color: '#CBD5E1' }}>—</span>,
    },
    {
      // Same rule as the User list (userManagement.jsx): active buddy links ARE the upline —
      // shown with each link's split % when there is more than one — and the account upline is
      // the fallback when there are no buddies. Each upline's team sits under the name, so a
      // recruit sitting in another team explains itself. (user 2026-09-17)
      id: 'upline', header: 'Upline', size: 190,
      accessorFn: r => {
        const buddies = r.buddyUplines ?? [];
        if (buddies.length) return buddies.map(b => `${b.name} ${b.group || ''}`).join(' ');
        return [r.uplineName, r.uplineGroup].filter(Boolean).join(' ');
      },
      Cell: ({ row }) => {
        const { uplineName, uplineGroup, buddyUplines } = row.original;
        const buddies = buddyUplines ?? [];
        const lines = buddies.length
          ? buddies.map(b => ({ name: b.name, group: b.group, pct: buddies.length > 1 ? Number(b.splitPercent) : null }))
          : (uplineName ? [{ name: uplineName, group: uplineGroup, pct: null }] : []);
        if (!lines.length) return <span style={{ color: '#CBD5E1' }}>—</span>;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {lines.map((l, i) => (
              <Box key={i} sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <Box sx={{ fontSize: '0.8rem' }}>
                  {l.name}
                  {l.pct != null && (
                    <span style={{ color: '#94A3B8', marginLeft: 4, fontSize: '0.7rem' }}>{l.pct.toFixed(0)}%</span>
                  )}
                </Box>
                {l.group && <Box sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>{l.group}</Box>}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      id: 'joinedOn', header: 'Joined', size: 110,
      accessorFn: r => r.joinedOn || '',
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v ? dayjs(v).format('DD MMM YYYY') : <span style={{ color: '#CBD5E1' }}>—</span>;
      },
    },
    {
      id: 'lastActive', header: 'Last Active', size: 130,
      accessorFn: r => r.lastActiveOn || '',
      Cell: ({ row }) => {
        const { lastActiveOn, inactiveDays } = row.original;
        if (!lastActiveOn) return <span style={{ color: '#CBD5E1' }}>Never</span>;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <Box sx={{ fontSize: '0.78rem' }}>{dayjs(lastActiveOn).format('DD MMM YYYY')}</Box>
            {inactiveDays != null && inactiveDays > 0 && (
              <Box sx={{ fontSize: '0.68rem', color: inactiveDays >= 14 ? '#B45309' : '#94A3B8' }}>
                {inactiveDays}d idle
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      id: 'lifetimeSales', header: 'Lifetime Sales', size: 150,
      accessorFn: r => r.lifetimeSalesCount ?? 0,
      Cell: ({ row }) => {
        const { lifetimeSalesCount: n, lifetimeSalesAmount: amt } = row.original;
        if (!n) return <span style={{ color: '#CBD5E1' }}>No sales yet</span>;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <Box sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{n} {n === 1 ? 'sale' : 'sales'}</Box>
            <Box sx={{ fontSize: '0.68rem', color: '#94A3B8' }}>{money(amt)}</Box>
          </Box>
        );
      },
    },
    {
      // Product quiz: best score and whether they passed, so an upline can see who has
      // actually learned the product. Sorts by best percentage; untried sorts last.
      id: 'quiz', header: 'Quiz Score', size: 140,
      accessorFn: r => (r.quizTotal ? r.quizScore / r.quizTotal : -1),
      Cell: ({ row }) => {
        const { quizScore: score, quizTotal: total, quizPassed: passed, quizAttempts: tries } = row.original;
        if (!total) return <span style={{ color: '#CBD5E1' }}>Not taken</span>;
        const pct = Math.round((score / total) * 100);
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, lineHeight: 1.3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{score}/{total}</Box>
              {pill(passed ? 'Passed' : 'NotPassed', QUIZ_STYLE)}
            </Box>
            <Box sx={{ fontSize: '0.68rem', color: '#94A3B8' }}>
              {pct}% · {tries} {tries === 1 ? 'try' : 'tries'}
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'approvalStatus', header: 'Approval', size: 130, filterVariant: 'select',
      accessorFn: r => r.approvalStatus || '',
      Cell: ({ cell }) => pill(cell.getValue(), APPROVAL_STYLE),
    },
    {
      id: 'status', header: 'Status', size: 120, filterVariant: 'select',
      accessorFn: r => r.status || '',
      Cell: ({ cell }) => pill(cell.getValue(), STATUS_STYLE, 'No account'),
    },
  ], []);

  const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
    columns,
    data: rows,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableStickyHeader: true,
    initialState: { density: 'compact', showGlobalFilter: true },
    state: {
      globalFilter: ts.globalFilter, sorting: ts.sorting,
      pagination: ts.pagination, columnFilters: ts.columnFilters, isLoading: loading,
    },
    onGlobalFilterChange: ts.setGlobalFilter,
    onSortingChange: ts.setSorting,
    onPaginationChange: ts.setPagination,
    onColumnFiltersChange: ts.setColumnFilters,
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableContainerProps: { sx: { overflowX: 'auto', maxHeight: 'unset' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search my team...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" } } },
    muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
    renderEmptyRowsFallback: () => (
      <Box sx={{ p: 4, textAlign: 'center', color: '#64748B', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }}>
        {error || 'No one has joined under you yet. People you invite appear here as soon as they apply.'}
      </Box>
    ),
  });

  const counts = useMemo(() => ({
    people: rows.length,
    active: rows.filter(r => r.status === 'Active').length,
    pending: rows.filter(r => r.approvalStatus === 'PendingReview').length,
  }), [rows]);

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
            My Team
          </h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
            The people you recruited, and where their application got to
          </p>
        </div>
        {!loading && counts.people > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`${counts.people} ${counts.people === 1 ? 'person' : 'people'}`} size="small"
              sx={{ backgroundColor: '#E0F2FE', color: '#0369A1', fontWeight: 600, fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif" }} />
            <Chip label={`${counts.active} active`} size="small"
              sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif" }} />
            {counts.pending > 0 && (
              <Chip label={`${counts.pending} pending review`} size="small"
                sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif" }} />
            )}
          </Box>
        )}
      </Box>

      <MaterialReactTable table={table} />
    </div>
  );
}
